/**
 * ClientsTable - Table component for displaying clients
 *
 * Shows sync progress via SSE events when available, falls back to spinner.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ContextMenu, MenuItem } from "@/components/MediaCard/ContextMenu";
import {
  ChartBarIcon,
  RefreshIcon,
  FacebookIcon,
  InstagramIcon,
  TikTokIcon,
  CopyIcon,
  CheckIcon,
  ChevronDownIcon,
  SpinnerIcon,
} from "@/components/icons";
import { EntityCreateButton } from "@/core/entityConfig";
import { ClientBadge } from "@/domains/Client/components/ClientBadge";
import { CareScoreBadge } from "@/components/ui/CareScoreBadge";
import { ClientStatusBadge } from "@/domains/Client/components/ClientStatusBadge";
import { useEntityJobSSE } from "@/hooks/useJobSSE";
import { apiClient } from "@/services/apiClient";
import { copyToClipboard } from "@/utils/clipboard";
import type {
  AgencyGrowth,
  GoogleClientSummaryMap,
  GoogleSummaryWindow,
} from "@/domains/Reporting/hooks/useGoogleClientSummaries";
import {
  changePct,
  changeTone,
  formatDayMonth,
  formatSignedPct,
  formatSignedRate,
  formatWindows,
  GROWTH_SOURCE_LABEL,
  growthTone,
  overdueNote,
  pillarSummary,
  type ChangeTone,
} from "@/domains/Reporting/utils/metricChange";
import type {
  Client,
  ClientStatus,
  FollowerStatsMap,
  ReportSummaryMap,
  DataHealthMap,
} from "@/types";

// Sales-pipeline base URL. Hardcoded to match the marketing site origin —
// promote to an env var if/when this needs to differ between environments.
const SALES_SITE_URL = "https://benerdy.it";

type SameDaysPair = GoogleSummaryWindow & { current: number | null; previous: number | null };

interface ClientsTableProps {
  clients: Client[];
  followerStats?: FollowerStatsMap;
  reportSummaries?: ReportSummaryMap;
  googleSummaries?: GoogleClientSummaryMap;
  agencyGrowth?: AgencyGrowth;
  /** The Google summaries request: the Growth and Google cells spin while it
   *  loads and say it failed, distinct from an unlinked source's dash. */
  googleSummariesState?: "loading" | "error" | "ready";
  sortedByGrowth?: boolean;
  onToggleGrowthSort?: () => void;
  dataHealth?: DataHealthMap;
  reportMonth?: number;
  reportYear?: number;
  onViewClient: (clientId: string) => void;
  onEditClient: (client: Client) => void;
  onArchiveClient?: (clientId: string) => void;
  onUnarchiveClient?: (clientId: string) => void;
  onStatusChange?: (clientId: string, status: ClientStatus) => void;
  onSyncData?: (clientId: string) => void;
  syncingClientId?: string | null;
  onCreateClient?: () => void;
}

/**
 * Status cell: clickable badge dropdown for ACTIVE/PROPOSAL, static pill for
 * ARCHIVED. When status=PROPOSAL, also exposes a small copy button for the
 * public proposal URL.
 */
function ClientStatusCell({
  client,
  onStatusChange,
}: {
  client: Client;
  onStatusChange?: (clientId: string, status: ClientStatus) => void;
}) {
  const [copiedUrl, setCopiedUrl] = useState(false);

  const status: ClientStatus = client.status ?? "ACTIVE";
  const isArchived = status === "ARCHIVED";
  // Prefer the high-entropy proposal_token; fall back to slug only for
  // older API responses pre-migration. Once the backend is rolled out
  // every PROPOSAL client has a token and the slug branch is unused.
  const proposalIdentifier = client.proposalToken ?? client.slug;
  const proposalUrl =
    status === "PROPOSAL" && proposalIdentifier
      ? `${SALES_SITE_URL}/proposta/${proposalIdentifier}`
      : null;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!proposalUrl) return;
    copyToClipboard(proposalUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <ClientStatusBadge
        status={status}
        onStatusChange={
          isArchived || !onStatusChange
            ? undefined
            : (next) => onStatusChange(client.id, next)
        }
      />
      {proposalUrl && (
        <button
          onClick={handleCopy}
          className={`p-1 rounded-md transition-colors ${
            copiedUrl
              ? "bg-green-100 text-green-600"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
          }`}
          title={copiedUrl ? "Copied!" : `Copy proposal URL: ${proposalUrl}`}
        >
          {copiedUrl ? (
            <CheckIcon className="w-3.5 h-3.5" />
          ) : (
            <CopyIcon className="w-3.5 h-3.5" />
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Actions cell for a client row - extracted to use SSE hook per-client.
 */
function ClientActionsCell({
  client,
  syncingClientId,
  onViewClient,
  onEditClient,
  onArchiveClient,
  onUnarchiveClient,
  onSyncData,
}: {
  client: Client;
  syncingClientId?: string | null;
  onViewClient: (clientId: string) => void;
  onEditClient: (client: Client) => void;
  onArchiveClient?: (clientId: string) => void;
  onUnarchiveClient?: (clientId: string) => void;
  onSyncData?: (clientId: string) => void;
}) {
  // Use SSE to get real-time sync progress
  const sseJob = useEntityJobSSE("client", client.id);
  const isSSESyncing = sseJob?.status === "running" && sseJob?.jobType === "social_sync";
  const isFallbackSyncing = syncingClientId === client.id;
  const isSyncing = isSSESyncing || isFallbackSyncing;
  const progressMessage = isSSESyncing ? sseJob?.progressMessage : null;

  const items: MenuItem[] = [
    {
      id: "open",
      label: "Open Workspace",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      ),
      onClick: () => onViewClient(client.id),
    },
    {
      id: "edit",
      label: "Edit Client",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      ),
      onClick: () => onEditClient(client),
    },
  ];

  if (onSyncData) {
    items.push({
      id: "sync",
      label: isSyncing ? (progressMessage || "Syncing...") : "Sync Social Data",
      icon: <RefreshIcon className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`} />,
      onClick: () => onSyncData(client.id),
      disabled: isSyncing,
    });
  }

  if (client.status === "ARCHIVED" && onUnarchiveClient) {
    items.push({
      id: "restore",
      label: "Restore Client",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
      onClick: () => onUnarchiveClient(client.id),
    });
  } else if (onArchiveClient) {
    items.push({
      id: "archive",
      label: "Archive Client",
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
          />
        </svg>
      ),
      onClick: () => onArchiveClient(client.id),
      variant: "danger",
    });
  }

  return <ContextMenu items={items} />;
}

export function ClientsTable({
  clients,
  followerStats,
  reportSummaries,
  googleSummaries,
  agencyGrowth,
  googleSummariesState = "ready",
  sortedByGrowth,
  onToggleGrowthSort,
  dataHealth,
  reportMonth,
  reportYear,
  onViewClient,
  onEditClient,
  onArchiveClient,
  onUnarchiveClient,
  onStatusChange,
  onSyncData,
  syncingClientId,
  onCreateClient,
}: ClientsTableProps) {
  const navigate = useNavigate();

  const [careScores, setCareScores] = useState<
    Record<string, { score: number; serviceScore?: number; velocityScore?: number; contactScore?: number }>
  >({});
  const [careTrends, setCareTrends] = useState<
    Record<string, { delta: number; previousScore: number }>
  >({});

  useEffect(() => {
    apiClient
      .get("/api/account/care-scores")
      .then((data: {
        scores?: Record<string, { score: number; serviceScore?: number; velocityScore?: number; contactScore?: number }>;
        trends?: Record<string, { delta: number; previousScore: number }>;
      }) => {
        if (data.scores) setCareScores(data.scores);
        if (data.trends) setCareTrends(data.trends);
      })
      .catch(() => {
        // Silently fail - care scores are supplementary
      });
  }, []);

  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null) return "-";
    return num.toLocaleString();
  };

  const formatCompact = (num: number) => {
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatDate = (dateStr: string | undefined | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatGrowth = (change: number | null | undefined) => {
    if (change === null || change === undefined) return null;
    const isPositive = change > 0;
    const isNegative = change < 0;
    return (
      <span
        className={`text-xs ${
          isPositive
            ? "text-green-600"
            : isNegative
            ? "text-red-600"
            : "text-gray-500"
        }`}
      >
        {isPositive ? "+" : ""}
        {change.toLocaleString()}
      </span>
    );
  };

  const growthChipClass: Record<ChangeTone, string> = {
    up: "bg-green-50 text-green-700",
    down: "bg-red-50 text-red-700",
    neutral: "text-gray-400",
  };
  const changeTextClass: Record<ChangeTone, string> = {
    up: "text-green-600",
    down: "text-red-600",
    neutral: "text-gray-500",
  };

  // A provisional rate is outlined, not filled: same tone, not yet final.
  const provisionalChipClass: Record<ChangeTone, string> = {
    up: "border border-green-300 text-green-700",
    down: "border border-red-300 text-red-700",
    neutral: "border border-gray-300 text-gray-500",
  };

  // The signed rate chip shared by the Growth column and the agency footer.
  // Its tone uses the footer's ±2 flat band, so a client counted flat is muted.
  const growthChip = (rate: number | null, title: string, provisional = false) => (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium tabular-nums ${
        (provisional ? provisionalChipClass : growthChipClass)[growthTone(rate)]
      }`}
      title={title}
    >
      {formatSignedRate(rate)}
    </span>
  );

  // Growth and Google cells before the summaries are in: a spinner (DD-001)
  // while loading, a lighter dash titled with the failure on error.
  const googleSummariesCell =
    googleSummariesState === "loading" ? (
      <SpinnerIcon className="w-3.5 h-3.5 text-gray-400" />
    ) : googleSummariesState === "error" ? (
      <span className="text-gray-300" title="Google summaries could not load">
        &ndash;
      </span>
    ) : null;

  // A source's to-date line pair under one name, whatever its line is called.
  const sameDaysPair = (
    window: GoogleSummaryWindow | null | undefined,
    current: number | null | undefined,
    previous: number | null | undefined,
  ): SameDaysPair | null =>
    window
      ? {
          through: window.through,
          previousThrough: window.previousThrough,
          current: current ?? null,
          previous: previous ?? null,
        }
      : null;

  // One stacked line of the Google column: "340 site sessions +5%", or a dash
  // when the source is not linked or has no rows for the month. A month that
  // has not settled compares its same-days windows, marked "to date", or
  // shows "partial" in place of the change without one, never a fall.
  const googleLine = (
    label: string,
    monthCurrent: number | null | undefined,
    monthPrevious: number | null | undefined,
    complete: boolean | undefined,
    toDate: SameDaysPair | null,
  ) => {
    if (monthCurrent == null) {
      return <div className="text-gray-400">- {label}</div>;
    }
    const sameDays = !complete && toDate?.current != null ? toDate : null;
    const current = sameDays?.current ?? monthCurrent;
    const pct = sameDays
      ? changePct(sameDays.current, sameDays.previous)
      : complete
      ? changePct(current, monthPrevious)
      : null;
    return (
      <div
        title={sameDays ? formatWindows(sameDays.through, sameDays.previousThrough) : undefined}
      >
        <span className="text-gray-900 font-medium">{formatCompact(current)}</span>{" "}
        <span className="text-gray-500">{label}</span>
        {!complete && (
          <span className="ml-1 text-gray-400">{sameDays ? "to date" : "partial"}</span>
        )}
        {pct != null && (
          <span className={`ml-1 ${changeTextClass[changeTone(pct)]}`}>
            {formatSignedPct(pct)}
          </span>
        )}
      </div>
    );
  };

  if (clients.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500 py-20">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p className="mt-2 text-sm font-medium text-gray-900">No clients yet</p>
          <p className="mt-1 text-sm text-gray-500">Create your first client to get started.</p>
          {onCreateClient && (
            <EntityCreateButton
              entity="client"
              onClick={onCreateClient}
              className="mt-4"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Client
            </th>
            <th className="px-3 xl:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex flex-col items-center">
                <span>Care</span>
                <span className="text-[9px] normal-case tracking-normal text-gray-400 font-normal">7-day trend</span>
              </div>
            </th>
            <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="hidden xl:table-cell px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Relationship Started
            </th>
            <th className="px-3 xl:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center justify-center gap-1">
                <FacebookIcon size={13} className="text-blue-600" />
              </div>
            </th>
            <th className="px-3 xl:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center justify-center gap-1">
                <InstagramIcon size={13} className="text-pink-600" />
              </div>
            </th>
            <th className="px-3 xl:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex items-center justify-center gap-1">
                <TikTokIcon size={13} className="text-gray-900" />
              </div>
            </th>
            <th className="hidden lg:table-cell px-3 xl:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button
                type="button"
                onClick={onToggleGrowthSort}
                disabled={!onToggleGrowthSort}
                className={`inline-flex items-center gap-0.5 uppercase tracking-wider ${
                  sortedByGrowth ? "text-gray-900" : "hover:text-gray-700"
                }`}
                title={sortedByGrowth ? "Sorted by growth · click to sort by name" : "Sort by growth"}
              >
                Growth
                {sortedByGrowth && <ChevronDownIcon className="w-3 h-3" />}
              </button>
            </th>
            <th className="hidden lg:table-cell px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Month Report
            </th>
            <th className="hidden lg:table-cell px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Google
            </th>
            <th className="hidden lg:table-cell px-3 xl:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Data Health
            </th>
            <th className="px-3 xl:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {clients.map((client) => {
            const stats = followerStats?.[client.id];

            return (
              <tr
                key={client.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onViewClient(client.id)}
              >
                <td className="px-3 xl:px-4 py-3 whitespace-nowrap">
                  <ClientBadge
                    name={client.clientName}
                    meta={
                      <>
                        {/* Sector · location · posting target — the client's
                            real profile fields, previously never surfaced. */}
                        {(client.sector || client.location || client.postsPerWeek) && (
                          <div className="text-xs text-gray-400 truncate">
                            {[
                              client.sector?.replace(/_/g, " "),
                              client.location,
                              client.postsPerWeek
                                ? `${client.postsPerWeek} post${client.postsPerWeek === 1 ? "" : "s"}/wk`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        )}
                        {client.links && client.links.length > 0 && (
                          <div className="flex gap-2 mt-0.5">
                            {client.links.slice(0, 2).map((link, idx) => (
                              <a
                                key={idx}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {link.name}
                              </a>
                            ))}
                          </div>
                        )}
                      </>
                    }
                  />
                </td>
                <td className="px-3 xl:px-4 py-3 whitespace-nowrap text-center">
                  {careScores[client.id] ? (
                    <div className="inline-flex flex-col items-center gap-1">
                      <CareScoreBadge
                        score={careScores[client.id].score}
                        showTooltip={false}
                        breakdown={{
                          serviceScore: careScores[client.id].serviceScore,
                          velocityScore: careScores[client.id].velocityScore,
                          contactScore: careScores[client.id].contactScore,
                        }}
                      />
                      {careTrends[client.id] && careTrends[client.id].delta !== 0 ? (
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            careTrends[client.id].delta > 0
                              ? "bg-green-50 text-green-700"
                              : "bg-red-50 text-red-700"
                          }`}
                          title={`Was ${careTrends[client.id].previousScore} a week ago`}
                        >
                          <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="currentColor">
                            {careTrends[client.id].delta > 0 ? (
                              <path d="M5 2L9 7H1L5 2Z" />
                            ) : (
                              <path d="M5 8L1 3H9L5 8Z" />
                            )}
                          </svg>
                          {Math.abs(careTrends[client.id].delta)}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-300 select-none">&mdash;</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">-</span>
                  )}
                </td>
                <td className="px-3 xl:px-4 py-3 whitespace-nowrap">
                  <ClientStatusCell client={client} onStatusChange={onStatusChange} />
                </td>
                <td className="hidden xl:table-cell px-3 xl:px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                  {formatDate(client.relationshipStartedAt)}
                </td>
                {(["facebook", "instagram", "tiktok"] as const).map((platform) => {
                  const platformStats = stats?.stats?.[platform];
                  const startKey = `${platform}_start_followers` as keyof Client;
                  const startVal = client[startKey] as number | undefined;

                  return (
                    <td key={platform} className="px-3 xl:px-4 py-3 whitespace-nowrap text-center">
                      {platformStats?.current ? (
                        <div className="text-sm">
                          <span className="text-gray-900">{formatCompact(platformStats.current)}</span>
                          {platformStats.monthChange != null && (
                            <div>{formatGrowth(platformStats.monthChange)}</div>
                          )}
                        </div>
                      ) : startVal ? (
                        <div className="text-sm text-gray-500">
                          {formatCompact(startVal)}
                          <span className="text-xs ml-0.5">(start)</span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm text-xs font-medium bg-red-100 text-red-500">
                          x
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="hidden lg:table-cell px-3 xl:px-4 py-3 whitespace-nowrap text-center">
                  {googleSummariesCell ?? (() => {
                    const google = googleSummaries?.[client.id];
                    const growth = google?.growth;
                    const overdue = growth ? overdueNote(growth.overdue) : null;
                    const withOverdue = (title: string) =>
                      overdue ? `${title} · ${overdue}` : title;
                    // A month in progress has no rate yet, only the day it turns final.
                    if (growth && !growth.complete && growth.rate == null) {
                      return (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium text-gray-400"
                          title={withOverdue(
                            growth.finalFrom
                              ? `final from ${formatDayMonth(growth.finalFrom)}`
                              : "not final yet",
                          )}
                        >
                          pending
                        </span>
                      );
                    }
                    // A provisional rate compares the same days of both months:
                    // the title names the windows of the Google sources that feed it.
                    if (growth && !growth.complete && growth.provisional) {
                      const windows = (
                        [
                          [GROWTH_SOURCE_LABEL.businessProfile, google?.businessProfile?.toDate],
                          [GROWTH_SOURCE_LABEL.website, google?.website?.toDate],
                        ] as const
                      )
                        .flatMap(([source, window]) =>
                          window
                            ? [`${source} ${formatWindows(window.through, window.previousThrough)} · `]
                            : [],
                        )
                        .join("");
                      const finalFrom = growth.finalFrom
                        ? ` · final from ${formatDayMonth(growth.finalFrom)}`
                        : "";
                      return growthChip(
                        growth.rate,
                        withOverdue(
                          `Provisional · ${windows}${pillarSummary(growth.pillars)}${finalFrom}`,
                        ),
                        true,
                      );
                    }
                    const title = growth ? withOverdue(pillarSummary(growth.pillars)) : "No growth data";
                    return growthChip(growth?.rate ?? null, title);
                  })()}
                </td>
                <td className="hidden lg:table-cell px-3 xl:px-4 py-3 whitespace-nowrap">
                  {(() => {
                    const summary = reportSummaries?.[client.id];
                    if (!summary) {
                      return (
                        <span className="text-gray-400 text-xs">No report</span>
                      );
                    }
                    return (
                      <div className="flex items-center gap-3">
                        <div className="text-xs space-y-0.5">
                          <div className="text-gray-900 font-medium">
                            {formatCompact(summary.totalEngagement)} eng
                          </div>
                          <div className="text-gray-500">
                            {formatCompact(summary.totalImpressions)} imp
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/app/${client.id}/reporting?year=${reportYear}&month=${reportMonth}`
                            );
                          }}
                          className="p-1 rounded-sm hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                          title="View full report"
                        >
                          <ChartBarIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })()}
                </td>
                <td className="hidden lg:table-cell px-3 xl:px-4 py-3 whitespace-nowrap">
                  {googleSummariesCell ?? (() => {
                    const google = googleSummaries?.[client.id];
                    return (
                      <div className="text-xs space-y-0.5">
                        {googleLine(
                          "profile actions",
                          google?.businessProfile?.actions,
                          google?.businessProfile?.actionsPrevious,
                          google?.businessProfile?.complete,
                          sameDaysPair(
                            google?.businessProfile?.toDate,
                            google?.businessProfile?.toDate?.actions,
                            google?.businessProfile?.toDate?.actionsPrevious,
                          ),
                        )}
                        {googleLine(
                          "site sessions",
                          google?.website?.sessions,
                          google?.website?.sessionsPrevious,
                          google?.website?.complete,
                          sameDaysPair(
                            google?.website?.toDate,
                            google?.website?.toDate?.sessions,
                            google?.website?.toDate?.sessionsPrevious,
                          ),
                        )}
                        {googleLine(
                          "ads clicks",
                          google?.googleAds?.clicks,
                          google?.googleAds?.clicksPrevious,
                          google?.googleAds?.complete,
                          sameDaysPair(
                            google?.googleAds?.toDate,
                            google?.googleAds?.toDate?.clicks,
                            google?.googleAds?.toDate?.clicksPrevious,
                          ),
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="hidden lg:table-cell px-3 xl:px-4 py-3 whitespace-nowrap">
                  {(() => {
                    const health = dataHealth?.[client.id];
                    if (!health) {
                      return (
                        <div className="flex justify-center">
                          <span className="text-gray-400 text-xs">-</span>
                        </div>
                      );
                    }

                    const indicators = [
                      { key: "followers", ok: health.hasFollowers, label: "F" },
                      { key: "activity", ok: health.hasAudienceActivity, label: "A" },
                      { key: "report", ok: health.hasReport, label: "R" },
                    ];

                    return (
                      <div className="flex justify-center gap-1">
                        {indicators.map(({ key, ok, label }) => (
                          <span
                            key={key}
                            className={`inline-flex items-center justify-center w-5 h-5 rounded text-xs font-medium ${
                              ok
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                            title={`${label === "F" ? "Followers" : label === "A" ? "Audience Activity" : "Report"}: ${ok ? "Synced" : "Missing"}`}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 xl:px-4 py-3 whitespace-nowrap text-right">
                  <ClientActionsCell
                    client={client}
                    syncingClientId={syncingClientId}
                    onViewClient={onViewClient}
                    onEditClient={onEditClient}
                    onArchiveClient={onArchiveClient}
                    onUnarchiveClient={onUnarchiveClient}
                    onSyncData={onSyncData}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {agencyGrowth && (
        <AgencyGrowthFooter
          growth={agencyGrowth}
          chip={
            // Outlined while clients are still settling: the median counts
            // final rates only and moves as they turn final.
            agencyGrowth.provisional > 0
              ? growthChip(
                  agencyGrowth.rate,
                  `${agencyGrowth.provisional} ${
                    agencyGrowth.provisional === 1 ? "client" : "clients"
                  } still settling · ${pillarSummary(agencyGrowth.pillars)}`,
                  true,
                )
              : growthChip(agencyGrowth.rate, pillarSummary(agencyGrowth.pillars))
          }
          changeTextClass={changeTextClass}
          monthLabel={
            reportYear && reportMonth
              ? new Date(reportYear, reportMonth - 1, 1).toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })
              : "last month"
          }
        />
      )}
    </div>
  );
}

/**
 * Agency growth, pinned under the rows as the table's totals (DD-042): the
 * median client rate, how many clients grew, held or declined, and each
 * pillar's median. Counts the agency's active clients, whatever the filter.
 */
function AgencyGrowthFooter({
  growth,
  chip,
  changeTextClass,
  monthLabel,
}: {
  growth: AgencyGrowth;
  chip: React.ReactNode;
  changeTextClass: Record<ChangeTone, string>;
  monthLabel: string;
}) {
  return (
    <div
      role="group"
      aria-label="Agency growth"
      className="sticky bottom-0 flex flex-wrap items-center gap-x-6 gap-y-1 bg-gray-50 px-4 py-3 text-sm"
    >
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
        Agency growth
      </span>
      {growth.rate == null ? (
        <span className="text-gray-400">
          No growth data for {monthLabel} yet
          {growth.provisional > 0 && <> · {growth.provisional} provisional</>}
        </span>
      ) : (
        <>
          {chip}
          <span className="text-gray-500">
            <span className="font-semibold text-gray-900">{growth.growing}</span> growing ·{" "}
            <span className="font-semibold text-gray-900">{growth.flat}</span> flat ·{" "}
            <span className="font-semibold text-gray-900">{growth.declining}</span> declining of{" "}
            {growth.clientsWithRate} active {growth.clientsWithRate === 1 ? "client" : "clients"} with data
            {growth.provisional > 0 && <> · {growth.provisional} provisional</>}
          </span>
          <span className="flex flex-wrap gap-x-3 text-xs">
            {growth.pillars.map((p) => (
              <span key={p.key} className="text-gray-500">
                {p.label}{" "}
                <span className={`font-medium tabular-nums ${changeTextClass[changeTone(p.changePct)]}`}>
                  {formatSignedRate(p.changePct)}
                </span>
              </span>
            ))}
          </span>
        </>
      )}
    </div>
  );
}
